import React, { useState } from 'react';
import { motion } from 'framer-motion';

const WaveChart = ({ data = [] }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartWidth = 640;
  const chartHeight = 220;
  const plotLeft = 50;
  const plotRight = 600;
  const plotTop = 20;
  const plotBottom = 200;
  const plotWidth = plotRight - plotLeft;
  const plotHeight = plotBottom - plotTop;

  // Filter months that have data (non-null averagePercentage)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const points = data.length > 0 ? data : monthNames.map((m) => ({ month: m, averagePercentage: null }));

  // Calculate x positions for all 12 months
  const getX = (idx) => plotLeft + (idx / 11) * plotWidth;
  const getY = (pct) => plotBottom - (pct / 100) * plotHeight;

  // Map null/undefined averagePercentage to 0 so we render them at 0%
  const validPoints = points.map((p, idx) => ({
    ...p,
    idx,
    averagePercentage: (p.averagePercentage === null || p.averagePercentage === undefined) ? 0 : p.averagePercentage
  }));

  let pathData = '';
  let fillPath = '';
  if (validPoints.length > 1) {
    // Build smooth curve using catmull-rom-to-bezier approximation
    const coords = validPoints.map((p) => [getX(p.idx), getY(p.averagePercentage)]);
    pathData = `M ${coords[0][0]} ${coords[0][1]}`;
    for (let i = 1; i < coords.length; i++) {
      const [x0, y0] = coords[i - 1];
      const [x1, y1] = coords[i];
      const cpx = (x0 + x1) / 2;
      pathData += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
    }
    fillPath = `${pathData} L ${coords[coords.length - 1][0]} ${plotBottom} L ${coords[0][0]} ${plotBottom} Z`;
  } else if (validPoints.length === 1) {
    const x = getX(validPoints[0].idx);
    const y = getY(validPoints[0].averagePercentage);
    pathData = `M ${x - 5} ${y} L ${x + 5} ${y}`;
    fillPath = '';
  }

  return (
    <svg width="100%" height="220" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#4F46E5" stopOpacity="0.00" />
        </linearGradient>
      </defs>

      {/* Grid Lines (Horizontal) */}
      <line x1={plotLeft} y1={plotTop} x2={plotRight} y2={plotTop} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="3 3" />
      <line x1={plotLeft} y1={65} x2={plotRight} y2={65} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="3 3" />
      <line x1={plotLeft} y1={110} x2={plotRight} y2={110} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="3 3" />
      <line x1={plotLeft} y1={155} x2={plotRight} y2={155} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="3 3" />
      <line x1={plotLeft} y1={plotBottom} x2={plotRight} y2={plotBottom} stroke="#E2E8F0" strokeWidth="1" />

      {/* Y Axis Labels */}
      <text x="11" y="24" style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}>100%</text>
      <text x="15" y="69" style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}>75%</text>
      <text x="15" y="114" style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}>50%</text>
      <text x="15" y="159" style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}>25%</text>
      <text x="22" y="204" style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}>0%</text>

      {/* Under-line Fill Area */}
      {fillPath && (
        <motion.path
          d={fillPath}
          fill="url(#waveGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        />
      )}

      {/* Line Stroke */}
      {pathData && (
        <motion.path
          d={pathData}
          fill="none"
          stroke="#4F46E5"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      )}

      {/* Data point dots */}
      {validPoints.map((p) => (
        <motion.circle
          key={p.idx}
          cx={getX(p.idx)}
          cy={getY(p.averagePercentage)}
          r="4"
          fill="#4F46E5"
          stroke="#fff"
          strokeWidth="2"
          initial={{ scale: 0 }}
          animate={{
            scale: hoveredPoint?.idx === p.idx ? 1.8 : 1
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 15,
            delay: hoveredPoint?.idx === p.idx ? 0 : (0.2 + p.idx * 0.05)
          }}
          style={{ pointerEvents: 'none' }}
        />
      ))}

      {/* Invisible Hover Targets */}
      {validPoints.map((p) => (
        <circle
          key={`target-${p.idx}`}
          cx={getX(p.idx)}
          cy={getY(p.averagePercentage)}
          r="16"
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => setHoveredPoint(p)}
          onMouseLeave={() => setHoveredPoint(null)}
        />
      ))}

      {/* Tooltip Card */}
      {hoveredPoint && (
        <g style={{ pointerEvents: 'none' }}>
          {/* Shadow/Border Card */}
          <rect
            x={getX(hoveredPoint.idx) - 50}
            y={getY(hoveredPoint.averagePercentage) - 48}
            width="100"
            height="32"
            rx="8"
            fill="#1e293b"
            filter="drop-shadow(0px 6px 12px rgba(15, 23, 42, 0.15))"
          />
          {/* Arrow */}
          <polygon
            points={`
              ${getX(hoveredPoint.idx) - 6},${getY(hoveredPoint.averagePercentage) - 17}
              ${getX(hoveredPoint.idx) + 6},${getY(hoveredPoint.averagePercentage) - 17}
              ${getX(hoveredPoint.idx)},${getY(hoveredPoint.averagePercentage) - 11}
            `}
            fill="#1e293b"
          />
          {/* Value Text */}
          <text
            x={getX(hoveredPoint.idx)}
            y={getY(hoveredPoint.averagePercentage) - 27}
            textAnchor="middle"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '11px',
              fontWeight: 700,
              fill: '#ffffff'
            }}
          >
            {`${hoveredPoint.month}: ${hoveredPoint.averagePercentage}%`}
          </text>
        </g>
      )}

      {/* X Axis Labels — all 12 months */}
      {points.map((p, idx) => (
        <text
          key={idx}
          x={getX(idx) - 10}
          y={218}
          style={{ fontFamily: 'Poppins', fontSize: 11, fill: '#94a3b8' }}
        >
          {p.month}
        </text>
      ))}
    </svg>
  );
};

export default WaveChart;
